/* This example requires Tailwind CSS v2.0+ */
import { useState } from "react";
import * as React from "react";
import { Link } from "gatsby";
import axios from "axios";
import useFormData from "./useFormData";
import { useLocalization } from "../../contexts/Localization";

export interface Props {
  close: () => void;
}

type FormData = {
  name: string;
  company: string;
  email: string;
  message: string;
};

type FormState = {
  isLoading: boolean;
  success: boolean;
  data: FormData;
};

const emptyForm = (): FormData => ({
  name: "",
  company: "",
  email: "",
  message: "",
});

const emptyFormState = (): FormState => ({
  isLoading: false,
  success: false,
  data: emptyForm(),
});

const QuoteForm: React.FC<Props> = ({ close }) => {
  const { language } = useLocalization();

  const formData = useFormData(language);
  const {
    recipient,
    nameLabel,
    companyLabel,
    emailLabel,
    messageLabel,
    submitLabel,
    successMessage,
    termsLink,
  } = formData;

  const [formState, setFormState] = useState(emptyFormState());

  function updateFormProperty<K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ) {
    setFormState((state) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    var html_message =
      "<h1>Nytt meddelande från hemsidan</h1> <br>" +
      "Email: " +
      formState.data.email +
      "<br>" +
      "Namn: " +
      formState.data.name +
      "<br>" +
      "Företag: " +
      formState.data.company +
      "<br>" +
      "Meddelande: " +
      formState.data.message +
      "<br><br>" +
      "Datum: " +
      new Date().toLocaleString();

    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));

    axios({
      method: "post",
      url: "/api/mail",
      data: {
        recipient: recipient || "edvard.mansson@metlab.se",
        html_message: html_message,
      },
    }).then((res) => {
      if (res.status !== 200) {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          error: res.data.error_msg,
        }));
      } else {
        setFormState((state) => ({
          ...state,
          isLoading: false,
          success: true,
        }));
      }
    });
  };

  return (
    <div>
      {!formState.success ? (
        <form action="#" method="POST" onSubmit={handleSubmit}>
          <div className="overflow-hidden sm:rounded-md">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="name"
                  className="block text-base font-medium text-gray-700"
                >
                  {nameLabel && nameLabel}*
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  autoComplete="given-name"
                  required
                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                  value={formState.data.name}
                  onChange={(e) => updateFormProperty("name", e.target.value)}
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="company"
                  className="block text-base font-medium text-gray-700"
                >
                  {companyLabel && companyLabel}
                </label>
                <input
                  type="text"
                  name="company"
                  id="company"
                  autoComplete="company"
                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                  value={formState.data.company}
                  onChange={(e) =>
                    updateFormProperty("company", e.target.value)
                  }
                />
              </div>

              <div className="col-span-6 sm:col-span-6">
                <label
                  htmlFor="email_address"
                  className="block text-base font-medium text-gray-700"
                >
                  {emailLabel && emailLabel}*
                </label>
                <input
                  type="email"
                  name="email_address"
                  id="email_address"
                  autoComplete="email"
                  required
                  className="mt-1 px-3 py-4 text-gray-700 focus:ring-metlab-500 focus:border-metlab-500 block w-full shadow-sm sm:text-base border rounded-md"
                  value={formState.data.email}
                  onChange={(e) => updateFormProperty("email", e.target.value)}
                />
              </div>

              <div className="col-span-5 sm:col-span-6">
                <label
                  htmlFor="message"
                  className="block text-base font-medium text-gray-700"
                >
                  {messageLabel && messageLabel}*
                </label>
                <textarea
                  name="message"
                  id="message"
                  autoComplete="email"
                  required
                  className="w-full px-3 py-4 text-gray-700 border sm:text-base rounded-md focus:ring-metlab-500 focus:border-metlab-500"
                  value={formState.data.message}
                  onChange={(e) =>
                    updateFormProperty("message", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 sm:px-6">
            <button
              type="submit"
              className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-full text-white bg-metlab-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metlab-500"
            >
              {submitLabel || "Skicka"}
            </button>
            <Link
              to={termsLink.url || ""}
              className="text-base text-gray-700 italic block mt-2 hover:text-metlab-500 cursor-pointer"
            >
              {termsLink.title && termsLink.title}
            </Link>
          </div>
        </form>
      ) : (
        <div className="text-base text-gray-700 italic block mt-2">
          {successMessage && successMessage}
        </div>
      )}
    </div>
  );
};

export default QuoteForm;
